import { Form } from "@inertiajs/react"
import { Package } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { transitionsMissionCollabPath } from "@/routes"
import type { BrandMissionCollabRow } from "@/types/missions"

import { ReturnToFields } from "./collab-transition-return"

/**
 * OKS-33 — Brand-side `Ready to ship` confirm dialog for the
 * Promotional workflow (UX Spec §2). Operator fires this once the
 * package is packed and ready to hand to the carrier. Lifts
 * `purchased → to_ship`; from there `mark_shipped` / `mark_delivered`
 * are shared with the Drop workflow.
 *
 * Mirrors `mark-purchased-confirm-dialog.tsx` shape — single Inertia
 * `<Form>` POST with hidden `event=mark_ready_to_ship`. No textarea
 * (notes are `flag_issue`-only).
 */
interface MarkReadyToShipConfirmDialogProps {
  row: BrandMissionCollabRow
  missionId: number
}

export function MarkReadyToShipConfirmDialog({
  row,
  missionId,
}: MarkReadyToShipConfirmDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ariaTitle = row.creator.handle
    ? `@${row.creator.handle}`
    : row.creator.name

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button
            type="button"
            variant="default"
            size="sm"
            aria-label={t("collabs.mark_ready_to_ship.aria", {
              title: ariaTitle,
            })}
            data-testid="brand-mission-collab-mark-ready-to-ship-trigger"
          >
            <Package aria-hidden="true" className="mr-1 size-4" />
            {t("collabs.mark_ready_to_ship.cta")}
          </Button>
        }
      />
      <DialogContent
        data-testid="brand-mission-collab-mark-ready-to-ship-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>{t("collabs.mark_ready_to_ship.title")}</DialogTitle>
          <DialogDescription>
            {t("collabs.mark_ready_to_ship.body", {
              creator: row.creator.name,
            })}
          </DialogDescription>
        </DialogHeader>
        <Form
          action={transitionsMissionCollabPath(missionId, row.id)}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ processing }) => (
            <>
              <input type="hidden" name="event" value="mark_ready_to_ship" />
              {/* OKS-1038 — return_to/collabs_shown on the crew member page only. */}
              <ReturnToFields />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  data-testid="brand-mission-collab-mark-ready-to-ship-cancel"
                >
                  {t("collabs.mark_ready_to_ship.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  disabled={processing}
                  autoFocus
                  data-testid="brand-mission-collab-mark-ready-to-ship-confirm"
                >
                  {t("collabs.mark_ready_to_ship.confirm")}
                </Button>
              </DialogFooter>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
